import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getPlatformAPI } from '../lib/platform'
import { supabase } from '../lib/supabase/client'
import { syncUserLanguage } from '../lib/supabase/auth'
import { useLanguage } from '../contexts/LanguageProvider'

/**
 * Hook to handle OAuth deep links (happytask://auth/callback#access_token=...)
 * Only works in Electron production builds - deep links don't work in dev mode
 */
export function useDeepLink() {
  const navigate = useNavigate()
  const platformAPI = getPlatformAPI()
  const { language } = useLanguage()

  useEffect(() => {
    // Only handle deep links in Electron
    if (platformAPI.platform !== 'electron' || !window.api) {
      return
    }

    const handleDeepLink = async (url: string) => {
      try {
        // Parse custom protocol URL manually (happytask://auth/callback#...)
        // Extract the hash part after #
        const hashIndex = url.indexOf('#')
        if (hashIndex === -1) {
          return
        }

        const hashPart = url.substring(hashIndex + 1)

        // Check if this contains access_token
        if (hashPart.includes('access_token')) {
          // Extract tokens from hash (access_token=...&refresh_token=...)
          const hashParams = new URLSearchParams(hashPart)
          const accessToken = hashParams.get('access_token')
          const refreshToken = hashParams.get('refresh_token')

          if (accessToken && refreshToken && supabase) {
            // Set session in Supabase
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            })

            if (error) {
              console.error('[DeepLink] Failed to set session:', error)
              return
            }

            if (data.session && data.user) {
              // Sprache in user_metadata setzen (Default: aktuelle App-Sprache)
              await syncUserLanguage(language)

              // Set storage mode to cloud-sync for OAuth users
              await platformAPI.setStorageMode('cloud-sync')

              // Mark login method and persistence
              localStorage.setItem('loginMethod', 'oauth')
              localStorage.setItem('hasLoggedInBefore', 'true')

              // Navigate based on profile completeness
              const hasFullName = data.user.user_metadata?.full_name
              const targetRoute = hasFullName ? '/home' : '/setup-profile'

              navigate(targetRoute, { replace: true })
            } else {
              console.error('[DeepLink] No session or user in response')
            }
          } else {
            console.error('[DeepLink] Missing tokens in URL')
          }
        }
      } catch (error) {
        console.error('[DeepLink] Error processing deep link:', error)
      }
    }

    // Register IPC listener for deep links from main process
    const cleanup = window.api.receive('deep-link', (url: string) => {
      if (!url) {
        console.error('[DeepLink] URL is undefined or null')
        return
      }
      handleDeepLink(url)
    })

    // Register IPC listener for password recovery deep links (safe IPC replacement for executeJavaScript)
    const cleanupRecovery = window.api.receive('deep-link-recovery', async (hashFragment: string) => {
      if (!hashFragment || typeof hashFragment !== 'string') return
      if (!/^[a-zA-Z0-9=&_\-.+%]+$/.test(hashFragment)) return

      // Set the localStorage flags BEFORE re-mounting SetupPage — its
      // authView state is initialized once from these on mount, not
      // reactively, so the flags must be in place before navigation.
      localStorage.setItem('passwordRecoveryMode', 'true')
      localStorage.setItem('passwordRecoveryHash', '#' + hashFragment)

      // Establish the Supabase session from the recovery tokens so the
      // subsequent `updateUser({ password })` call inside ResetPasswordForm
      // is authorised. Without this, only the localStorage flag is set —
      // the user lands on the form but submitting fails.
      const params = new URLSearchParams(hashFragment)
      const accessToken = params.get('access_token')
      const refreshToken = params.get('refresh_token')
      if (accessToken && refreshToken && supabase) {
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        })
        if (error) {
          console.error('[DeepLink] Failed to set recovery session:', error)
        } else if (data.user?.id) {
          // is_active gate — even an old recovery link generated BEFORE
          // the send-email-hook block landed must not let a deactivated
          // account through to the new-password form.
          const { data: profile } = await supabase
            .from('profiles')
            .select('is_active')
            .eq('id', data.user.id)
            .maybeSingle()
          if (profile?.is_active === false) {
            await supabase.auth.signOut()
            localStorage.removeItem('passwordRecoveryMode')
            localStorage.removeItem('passwordRecoveryHash')
            window.location.reload()
            return
          }
        }
      }

      // Force a full reload so SetupPage re-mounts and re-runs its
      // `authView` useState initializer (which reads passwordRecoveryMode
      // from localStorage). React-Router's `navigate('/')` is a no-op
      // when the user is already on `/` — e.g. sitting on the
      // "Überprüfe deinen Posteingang" forgotPassword view — and the
      // page would silently keep showing the old view until the user
      // manually restarted the app. A reload mirrors that restart.
      // Critically, do NOT write the recovery payload into
      // `window.location.hash` — HashRouter would interpret
      // `#access_token=…` as a route path and log
      // "No routes matched location /access_token=…", leaving the
      // renderer blank.
      window.location.reload()
    })

    // Cleanup on unmount
    return () => {
      cleanup?.()
      cleanupRecovery?.()
    }
  }, [navigate, platformAPI, language])
}
